import { Flex, Box } from '@chakra-ui/react';
import { Banner, Button, useToast } from '@morressier/alembic';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { emailContentSchema } from '@publishing-engine/domain/validation';
import { IDecisionModalProps } from './decisionModalHelper';
import { createEditorialModal } from './EditorialDecisionModalWrapper';
import { EditorialNote, useEditorialDecisionNote } from './EditorialNote';
import { useCommandEmailTemplates } from 'src/hooks/useCommandEmailTemplates';
import { useSteps } from './hooks';
import StepHeader from '../../../pages/Organization/public/Journal/SubmissionUpload/components/StepHeader';
import { Overlay } from '../../Overlay';
import ComposeDecisionEmail, { EmailContentForm } from './ComposeDecisionEmail';
import { buildEmailPayload } from '../../../utils/normalizeEmailRecipients';
import { useAttachmentSync } from '../../../hooks/useAttachmentSync';
import useEditorialForm from 'src/hooks/useEditorialForm';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useFormalAcceptance } from 'src/api/commands';
import { useSubmissionContextData } from '../../../contexts/SubmissionContextProvider';
import DecisionLayout from './DecisionLayout';
import { useEffect, useState } from 'react';
import NotifyReviewersToggle from './NotifyReviewersToggle';

export const AcceptForFormalAcceptanceModal = createEditorialModal(
  ({ submissionId, submissionContextId, onClose }: IDecisionModalProps) => {
    const toast = useToast();
    const [notifyReviewers, setNotifyReviewers] = useState(false);
    const [notifyReviewerIds, setNotifyReviewerIds] = useState<string[]>([]);

    const {
      changeRequestData,
      refetchSubmission,
      workflowResponse: { articleWorkflow },
      organizationId,
      icmjeForm,
      icmjeFormList,
      workflowResponse: { manuscriptId }
    } = useSubmissionData();

    const { submissionContext } = useSubmissionContextData();

    const { hasCustomizableTemplates, templates } = useCommandEmailTemplates({
      organizationId,
      submissionContextId,
      commandName: 'AcceptForFormalAcceptance'
    });

    const { currentStep, goToNextStep, goToPreviousStep } = useSteps(
      hasCustomizableTemplates ? 2 : 0
    );

    const { editorNote, setEditorNote, submitEditorNote } =
      useEditorialDecisionNote({
        submissionId,
        submissionContextId,
        organizationId
      });

    const emailContentMethods = useForm<EmailContentForm>({
      defaultValues: {
        emailContent: {
          body: '',
          subject: '',
          recipients: { to: [], cc: [], bcc: [] },
          attachments: []
        }
      },
      resolver: zodResolver(emailContentSchema)
    });
    const emailContent = emailContentMethods.watch('emailContent');
    const { setValue } = emailContentMethods;
    useEffect(() => {
      const defaultIndex = templates.findIndex(item => item.default);
      const selectedIndex = defaultIndex === -1 ? 0 : defaultIndex;
      const template = templates?.[selectedIndex || 0];

      if (!template) return;

      setValue(
        'emailContent.recipients.to',
        template.recipients.to
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );

      setValue(
        'emailContent.recipients.cc',
        template.recipients.cc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue(
        'emailContent.recipients.bcc',
        template.recipients.bcc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue('emailContent.subject', template.subject);
      setValue('emailContent.body', template.body);
    }, [templates.length]);

    // Auto-sync attachments from first step to email step
    useAttachmentSync({
      sourceForm: emailContentMethods,
      targetForm: emailContentMethods,
      sourceField: 'attachments',
      targetField: 'emailContent.attachments'
    });

    const formalAcceptanceMutation = useFormalAcceptance(
      submissionId,
      submissionContextId,
      refetchSubmission,
      organizationId,
      buildEmailPayload(emailContent),
      notifyReviewerIds
    );
    const {
      editorialFormSchema: preProductionFormSchema,
      editorialFormData: preProductionFormData
    } = useEditorialForm(
      submissionId,
      submissionContextId,
      articleWorkflow.organizationId,
      'preProduction',
      articleWorkflow.id
    );

    const handleConfirmDecision = async () => {
      const formFields = preProductionFormSchema?.formFields ?? [];
      const caseData = preProductionFormData?.caseData ?? {};
      const isAllFieldsCompleted = formFields
        .filter(({ params }) => params.validations.required)
        .every(({ name }) => Boolean(caseData[name]));

      if (!isAllFieldsCompleted) {
        toast({
          title: 'Task Checklist Incomplete',
          description:
            'According to your Pre-Production checklist, some items are still left to check. Please make sure all checks were completed before proceeding.',
          variant: 'error',
          isClosable: true
        });
        onClose();
        return;
      }

      const totalRequestedFields =
        changeRequestData?.changeRequest?.requestHistory?.flatMap(
          history => history.fields
        ).length || 0;

      if (totalRequestedFields !== changeRequestData?.totalAddressedRequests) {
        toast({
          title: 'Change Requests Pending',
          description:
            'The author must address any of your pending change requests before proceeding to Formal Acceptance.',
          variant: 'error'
        });
        onClose();
        return;
      }

      if (
        submissionContext?.config.authorICMJEModel === 'SubmittingAuthorOnly'
      ) {
        if (
          !icmjeForm?.length ||
          (icmjeForm?.length && !icmjeForm[0]?.file.url)
        ) {
          toast({
            title: 'Missing Forms and Disclosures',
            description:
              'Please, make sure that Forms and Disclosures are provided for this submission before proceeding.',
            variant: 'error',
            isClosable: true
          });
          onClose();
          return;
        }
      }

      if (
        submissionContext?.config.authorICMJEModel === 'SubmittingAuthorForAll'
      ) {
        if (
          !icmjeForm?.length ||
          !icmjeForm[0]?.file.url ||
          icmjeFormList?.filter(form => form.isSubmitted === false).length
        ) {
          toast({
            title: 'Missing Forms and Disclosures',
            description:
              'Please, make sure that Forms and Disclosures are provided for this submission before proceeding.',
            variant: 'error',
            isClosable: true
          });
          onClose();
          return;
        }
      }

      const res = await formalAcceptanceMutation.mutateAsync();
      if (!res.isSuccess) {
        toast({
          variant: 'error',
          title: 'Error accepting for Publication',
          description:
            'There was an error while accepting submission for Publication.'
        });
        return;
      }
      await submitEditorNote(editorNote);

      toast({
        title: 'Submission Accepted for Publication',
        description:
          'The author and Reviewers have been notified via email about this decision',
        variant: 'success',
        isClosable: true
      });
      onClose();
    };

    const onSubmitFirstStep: SubmitHandler<any> = (_data, event) => {
      const nativeEvent = event?.nativeEvent as SubmitEvent | undefined;
      const submitter = nativeEvent?.submitter as HTMLButtonElement | null;
      if (!submitter) return;
      const action = submitter.value;
      if (hasCustomizableTemplates && action === 'EditMail') {
        goToNextStep();
      } else {
        handleConfirmDecision();
      }
    };

    const onSubmitEmailStep = () => {
      handleConfirmDecision();
    };

    const renderFirstStep = () => (
      <FormProvider {...emailContentMethods}>
        <form
          onSubmit={e => {
            e.preventDefault();
            onSubmitFirstStep(null, e);
          }}
        >
          <Box marginX="auto">
            <StepHeader
              currentStep={1}
              totalSteps={hasCustomizableTemplates ? 2 : 0}
              heading="Proceed to Formal Acceptance"
              description="Proceeding confirms this Submission is ready for export. The
                  Author will be notified of this decision and their next steps."
              marginY={2}
            />
          </Box>
          <Box paddingBottom={8}>
            <Flex flexDirection="column" gap={4}>
              <Banner
                content={
                  <ul>
                    <li>All checks are complete;</li>
                    <li>The Submission is final and ready for production; </li>
                    <li>
                      The author will be notified of this decision and their
                      next steps.
                    </li>
                  </ul>
                }
                heading="By proceeding, you confirm that..."
                variant="attention"
              />

              <EditorialNote value={editorNote} onChange={setEditorNote} />
            </Flex>
          </Box>
        </form>
      </FormProvider>
    );

    const renderEmailStep = () => (
      <FormProvider {...emailContentMethods}>
        <form onSubmit={emailContentMethods.handleSubmit(onSubmitEmailStep)}>
          <Box marginX="auto">
            <StepHeader
              currentStep={2}
              totalSteps={2}
              heading="Send Email Notification"
              description="Compose an email to notify the author about the formal acceptance."
              marginY={2}
            />
          </Box>
          <ComposeDecisionEmail
            organizationId={organizationId}
            submissionContextId={submissionContextId}
            commandName="AcceptForFormalAcceptance"
          />
          <div style={{ display: 'none' }}>
            <EditorialNote value={editorNote} onChange={setEditorNote} />
          </div>
        </form>
      </FormProvider>
    );

    const renderReviewerNotification = () => (
      <NotifyReviewersToggle
        submissionId={submissionId}
        organizationId={organizationId}
        submissionContextId={submissionContextId}
        commandName="AcceptForFormalAcceptance"
        isEnabled={notifyReviewers}
        selectedReviewerIds={notifyReviewerIds}
        onChange={(enabled, reviewerIds) => {
          setNotifyReviewers(enabled);
          setNotifyReviewerIds(reviewerIds);
        }}
      />
    );

    const renderFooter = () => {
      if (currentStep === 1) {
        return (
          <Flex
            paddingY={4}
            paddingX={6}
            bottom={0}
            zIndex={1}
            width="100%"
            justifyContent="space-between"
            position="sticky"
            bgColor="surfacePrimaryFill"
          >
            <Button variant="caution" onClick={onClose}>
              Exit
            </Button>
            <Flex gap={3}>
              {hasCustomizableTemplates && (
                <Button onClick={goToNextStep} variant="secondary">
                  {'Edit Mail & Submit Decision'}
                </Button>
              )}
              <Button
                onClick={handleConfirmDecision}
                isLoading={
                  !hasCustomizableTemplates &&
                  formalAcceptanceMutation.isPending
                }
              >
                {'Submit Decision'}
              </Button>
            </Flex>
          </Flex>
        );
      }

      return (
        <Flex
          paddingY={4}
          paddingX={6}
          bottom={0}
          zIndex={1}
          width="100%"
          justifyContent="space-between"
          position="sticky"
          bgColor="surfacePrimaryFill"
        >
          <Button onClick={goToPreviousStep} variant="secondary">
            Back
          </Button>
          <Button
            onClick={() =>
              emailContentMethods.handleSubmit(onSubmitEmailStep)()
            }
            isLoading={formalAcceptanceMutation.isPending}
          >
            {'Submit Decision'}
          </Button>
        </Flex>
      );
    };

    return (
      <Overlay backgroundColor="surfaceSecondaryFill">
        <DecisionLayout manuscriptId={manuscriptId} onClose={onClose}>
          <Box marginTop={2}>
            {currentStep === 1
              ? renderFirstStep()
              : hasCustomizableTemplates
              ? renderEmailStep()
              : null}
            {renderReviewerNotification()}
          </Box>
        </DecisionLayout>
        {renderFooter()}
      </Overlay>
    );
  }
);
